import { ref } from 'vue';
import { useRuntimeConfig, useCookie } from '#imports';
import axios from 'axios';
import { useMainStore } from '../stores/mainStore';
import { useFeedbackStore } from '../stores/feedbackStore';
import { useCmsAiStore } from '../stores/cmsAiStore.js';

export function useFeedback() {
  const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
  const cmsAiStore = useCmsAiStore();

  const isLoading = ref(false);

  /**
   * Validates feedback data and required store values
   * @param {Object} feedbackData - The feedback data object
   * @param {string} sentiment - The feedback sentiment
   * @returns {Object|null} Returns object with validated values (secondary_organization_id, query_id, session_id), or null if validation fails
   */
  const validate = (feedbackData, sentiment) => {
    if (!feedbackData || !sentiment) {
      console.error('Feedback data or sentiment not found');
      return null;
    }
    if (!feedbackData.id || !feedbackData.type) {
      console.error('Subject ID and type are required');
      return null;
    }

    const mainStore = useMainStore();

    const secondary_organization_id = mainStore.getSecondaryOrgId;
    if (!secondary_organization_id) {
      console.error('Secondary organization ID not found');
      return null;
    }

    const query_id = cmsAiStore.getSearchUUID;
    if (!query_id) {
      console.error('Query ID not found');
      return null;
    }

    const session_id = cmsAiStore.getSessionId;
    if (!session_id) {
      console.error('Session ID not found');
      return null;
    }

    return {
      secondary_organization_id,
      query_id,
      session_id,
    };
  };

  /**
   * Builds the request payload and headers
   * @param {Object} feedbackData - The feedback data object
   * @param {string} sentiment - The feedback sentiment
   * @param {string} additionalDetails - Additional feedback details
   * @param {string} query_id - The query ID
   * @param {string} session_id - The session ID
   * @returns {Object} Returns object with payload and headers
   */
  const buildPayload = (feedbackData, sentiment, additionalDetails, query_id, session_id) => {
    const payload = {
      feedback: {
        subject_id: feedbackData.id,
        subject_type: feedbackData.type,
        sentiment: sentiment,
        category: feedbackData.category,
        query_id: query_id,
        session_id: session_id,
        search_result_position: feedbackData.result_position,
        subject_display_text: feedbackData.display_text,
        additional_details: additionalDetails.length > 0 ? additionalDetails : '',
      },
    };

    const { value: token } = useCookie('auth-token');
    const headers = token ? { Authorization: `${token}` } : {};

    return { payload, headers };
  };

  const submitFeedback = async (feedbackData = {}, sentiment = '', additionalDetails = '') => {
    const validated = validate(feedbackData, sentiment);
    if (!validated) {
      return;
    }

    const feedbackStore = useFeedbackStore();
    feedbackStore.setFeedbackData(feedbackData);
    feedbackStore.setFeedbackSentiment(sentiment);

    try {
      isLoading.value = true;

      const { secondary_organization_id, query_id, session_id } = validated;
      const { payload, headers } = buildPayload(feedbackData, sentiment, additionalDetails, query_id, session_id);

      await axios.post(`${writableApi}api/v4/o/${secondary_organization_id}/cms/public/user_feedback`, payload, {
        headers,
      });
    } catch (err) {
      console.error('Error submitting feedback:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const deleteFeedback = async (feedbackData = {}, sentiment = '', additionalDetails = '') => {
    const validated = validate(feedbackData, sentiment);
    if (!validated) {
      return;
    }

    const feedbackStore = useFeedbackStore();
    feedbackStore.setFeedbackData(null);
    feedbackStore.setFeedbackSentiment(null);

    try {
      isLoading.value = true;

      const { secondary_organization_id, query_id, session_id } = validated;
      const { payload, headers } = buildPayload(feedbackData, sentiment, additionalDetails, query_id, session_id);

      await axios.delete(`${writableApi}api/v4/o/${secondary_organization_id}/cms/public/user_feedback`, {
        data: payload,
        headers,
      });
    } catch (err) {
      console.error('Error deleting feedback:', err);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    submitFeedback,
    deleteFeedback,
  };
}
